import * as React from 'react';
import {trackConversion} from '../components/PageMetadata';
import {Suspense, useEffect, useMemo, useState, useTransition} from 'react';
import {graphql, useLazyLoadQuery, useMutation} from 'react-relay';
import {useNavigate} from 'react-router-dom';
import {Check, Minus, Plus, ShoppingBag, X} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {FacebookLoginForm} from '../components/AuthMenu';
import {Cart, CartItem, clearCart, isoDate, loadCart, saveCart} from '../cart';
import {BookPageCatalogQuery} from './__generated__/BookPageCatalogQuery.graphql';
import {BookPageRequestMutation} from './__generated__/BookPageRequestMutation.graphql';

const CATALOG = graphql`
    query BookPageCatalogQuery($startOn: ISO8601Date!, $endOn: ISO8601Date!, $category: EquipmentCategory) {
        rentalItems(first: 200, category: $category) {
            nodes {
                id
                assetTag
                name
                description
                category
                image
                specifications
                dailyRate
                kitMembers { id }
                available: availableOn(startOn: $startOn, endOn: $endOn)
            }
        }
        viewer { id name }
        facebookLoginEnabled
    }`;

const REQUEST = graphql`
    mutation BookPageRequestMutation($input: RequestReservationInput!) {
        requestReservation(input: $input) {
            reservation { id }
            userErrors { message assetTag }
        }
    }`;

const CATEGORIES = ['KIT', 'CAMERA', 'LENS', 'CAMERA_ACCESSORY', 'LIGHTING', 'AUDIO_VIDEO', 'MEDIA', 'BATTERY'] as const;

// Rental days as the shop bills them: pickup morning through return evening, inclusive.
const rentalDays = (startOn: string, endOn: string) =>
    Math.max(1, Math.round((new Date(endOn).getTime() - new Date(startOn).getTime()) / 86_400_000) + 1);

const formatDay = (iso: string) =>
    new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, {weekday: 'short', month: 'short', day: 'numeric'});

const Catalog: React.FC<{
    cart: Cart; category: string;
    onAdd: (item: CartItem) => void; onRemove: (tag: string) => void;
    onViewer: (viewer: { id: string; name: string | null } | null, loginEnabled: boolean) => void;
}> = ({cart, category, onAdd, onRemove, onViewer}) => {
    const data = useLazyLoadQuery<BookPageCatalogQuery>(CATALOG, {
        startOn: cart.startOn, endOn: cart.endOn, category: (category || null) as BookPageCatalogQuery['variables']['category'],
    }, {fetchPolicy: 'store-and-network'});

    useEffect(() => {
        onViewer(data.viewer ? {id: data.viewer.id, name: data.viewer.name ?? null} : null, data.facebookLoginEnabled);
    }, [data.viewer, data.facebookLoginEnabled, onViewer]);

    const items = (data.rentalItems.nodes ?? []).flatMap((n) => (n ? [n] : []));
    const inCart = new Set(cart.items.map((i) => i.assetTag));

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {items.map((item) => {
                const chosen = inCart.has(item.assetTag);
                return (
                    <div key={item.id}
                         className={`group bg-[#050505] border p-4 flex flex-col transition-colors ${
                             chosen ? 'border-lumina-accent' : item.available ? 'border-gray-800 hover:border-lumina-purple/50' : 'border-gray-900 opacity-60'
                         }`}>
                        <div className="relative aspect-[4/3] overflow-hidden mb-3 bg-gray-900">
                            {item.image ? (
                                <img src={item.image} alt={item.name} className="w-full h-full object-contain bg-white p-2"/>
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-700 font-display text-[10px] tracking-widest">{item.assetTag}</div>
                            )}
                            <div className="absolute top-2 left-2 bg-lumina-purple/90 text-white text-[10px] font-bold px-2 py-0.5 uppercase">
                                {item.category.replace(/_/g, ' ')}
                            </div>
                            {!item.available && (
                                <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-xs font-bold tracking-widest text-gray-300">
                                    BOOKED THESE DATES
                                </div>
                            )}
                        </div>
                        <h3 className="text-white font-display font-bold leading-tight">{item.name}</h3>
                        {item.kitMembers.length > 0 && <p className="text-[11px] text-gray-500">{item.kitMembers.length} pieces</p>}
                        {item.specifications.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                                {item.specifications.map((s, i) => (
                                    <span key={i} className="text-[10px] text-gray-400 bg-gray-900 px-1.5 py-0.5 border border-gray-800">{s}</span>
                                ))}
                            </div>
                        )}
                        <div className="flex items-end justify-between mt-auto pt-3 border-t border-gray-800 mt-3">
                            {/* Fixed height so every tile's footer lines up. */}
                            <span className="text-sm h-8 flex items-end leading-none">
                                {item.dailyRate
                                    ? <span className="text-lumina-accent font-display font-bold">${item.dailyRate}<span className="text-gray-600 text-xs font-sans font-normal">/day</span></span>
                                    : <span className="text-gray-500 text-xs">pricing on request</span>}
                            </span>
                            {chosen ? (
                                <button onClick={() => onRemove(item.assetTag)}
                                        className="w-8 h-8 flex items-center justify-center bg-lumina-accent text-black" title="Remove from request">
                                    <Minus className="w-4 h-4"/>
                                </button>
                            ) : (
                                <button disabled={!item.available}
                                        onClick={() => onAdd({assetTag: item.assetTag, name: item.name, dailyRate: item.dailyRate, pieces: item.kitMembers.length})}
                                        className="w-8 h-8 flex items-center justify-center bg-white text-black hover:bg-lumina-accent disabled:opacity-30 disabled:cursor-not-allowed"
                                        title="Add to request">
                                    <Plus className="w-4 h-4"/>
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}
            {items.length === 0 && <p className="text-gray-500 text-sm col-span-full">Nothing in this category yet.</p>}
        </div>
    );
};

const BookPage: React.FC = () => {
    const navigate = useNavigate();
    const [cart, setCart] = useState<Cart>(loadCart);
    const [category, setCategory] = useState('');
    const [notes, setNotes] = useState('');
    const [phone, setPhone] = useState('');
    const [viewer, setViewer] = useState<{ id: string; name: string | null } | null>(null);
    const [loginEnabled, setLoginEnabled] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    const [, startTransition] = useTransition();
    const [commit, inFlight] = useMutation<BookPageRequestMutation>(REQUEST);

    useEffect(() => { saveCart(cart); }, [cart]);

    const onViewer = React.useCallback((v: { id: string; name: string | null } | null, enabled: boolean) => {
        setViewer(v);
        setLoginEnabled(enabled);
    }, []);

    const datesValid = cart.startOn >= isoDate(new Date()) && cart.endOn >= cart.startOn;
    const days = rentalDays(cart.startOn, cart.endOn);
    const estimate = useMemo(() => {
        const priced = cart.items.filter((i) => i.dailyRate);
        const total = priced.reduce((sum, i) => sum + parseFloat(i.dailyRate!) * days, 0);
        return {total, unpriced: cart.items.length - priced.length};
    }, [cart.items, days]);

    const setDates = (patch: Partial<Pick<Cart, 'startOn' | 'endOn'>>) =>
        startTransition(() => setCart((c) => {
            const next = {...c, ...patch};
            if (next.endOn < next.startOn) next.endOn = next.startOn;
            return next;
        }));

    const submit = () => {
        setErrors([]);
        commit({
            variables: {input: {startOn: cart.startOn, endOn: cart.endOn, assetTags: cart.items.map((i) => i.assetTag), notes: notes || null, phone: phone || null}},
            onCompleted: (res) => {
                const payload = res.requestReservation;
                if (payload?.reservation) {
                    trackConversion('generate_lead', 'rental_request');
                    clearCart();
                    navigate(`/client/reservations/${encodeURIComponent(payload.reservation.id)}?requested=1`);
                } else {
                    setErrors(payload?.userErrors.map((u) => u.message) ?? ['Something went wrong']);
                }
            },
            onError: (err) => setErrors([err.message]),
        });
    };

    return (
        <div className="bg-lumina-dark min-h-screen text-white">
            <Navbar/>
            <main className="max-w-7xl mx-auto px-6 pt-28 pb-24">
                <div className="mb-8">
                    <span className="text-lumina-purple font-display text-sm tracking-wider uppercase">Gear Locker</span>
                    <h1 className="text-4xl md:text-5xl font-display font-bold mt-2">BOOK <span className="text-gray-600">GEAR</span></h1>
                    <p className="text-gray-400 mt-3 max-w-2xl">Pick your dates, add kits or individual pieces, and send us a request. We confirm every booking by hand and get back to you with pricing for anything not listed.</p>
                </div>

                <div className="flex flex-col lg:flex-row gap-8">
                    <div className="flex-1">
                        <div className="flex flex-col md:flex-row gap-3 md:items-end mb-6">
                            <label className="text-xs text-gray-500 uppercase tracking-wider">
                                Pickup
                                <input type="date" value={cart.startOn} min={isoDate(new Date())}
                                       onChange={(e) => setDates({startOn: e.target.value})}
                                       className="block mt-1 bg-black border border-gray-800 px-3 py-2 text-sm text-white focus:border-lumina-purple outline-none"/>
                            </label>
                            <label className="text-xs text-gray-500 uppercase tracking-wider">
                                Return
                                <input type="date" value={cart.endOn} min={cart.startOn}
                                       onChange={(e) => setDates({endOn: e.target.value})}
                                       className="block mt-1 bg-black border border-gray-800 px-3 py-2 text-sm text-white focus:border-lumina-purple outline-none"/>
                            </label>
                            <span className="text-sm text-gray-400 md:pb-2">{days} day{days === 1 ? '' : 's'} · pickup from 9am, back by 5pm</span>
                            <select value={category} onChange={(e) => setCategory(e.target.value)}
                                    className="md:ml-auto bg-black border border-gray-800 px-3 py-2 text-sm focus:border-lumina-purple outline-none">
                                <option value="">All gear</option>
                                {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
                            </select>
                        </div>
                        {datesValid ? (
                            <Suspense fallback={<p className="text-gray-500 text-sm">Checking availability…</p>}>
                                <Catalog cart={cart} category={category} onViewer={onViewer}
                                         onAdd={(item) => setCart((c) => ({...c, items: [...c.items, item]}))}
                                         onRemove={(tag) => setCart((c) => ({...c, items: c.items.filter((i) => i.assetTag !== tag)}))}/>
                            </Suspense>
                        ) : (
                            <p className="text-red-400 text-sm">Pick a pickup date from today onwards and a return date on or after it.</p>
                        )}
                    </div>

                    <aside className="lg:w-80 shrink-0">
                        <div className="lg:sticky lg:top-28 border border-gray-800 bg-[#050505] p-5">
                            <h2 className="font-display font-bold flex items-center gap-2 mb-4"><ShoppingBag className="w-4 h-4 text-lumina-accent"/> Your request</h2>
                            <p className="text-xs text-gray-500 mb-3">{formatDay(cart.startOn)} → {formatDay(cart.endOn)} · {days} day{days === 1 ? '' : 's'}</p>
                            {cart.items.length === 0 && <p className="text-gray-500 text-sm">Nothing yet — add gear from the left.</p>}
                            <ul className="divide-y divide-gray-900 mb-4">
                                {cart.items.map((item) => (
                                    <li key={item.assetTag} className="py-2 flex items-start gap-2 text-sm">
                                        <span className="flex-1">
                                            {item.name}
                                            {item.pieces > 0 && <span className="text-gray-500 text-xs"> · {item.pieces} pieces</span>}
                                            <span className="block text-xs text-gray-500">
                                                {item.dailyRate ? `$${(parseFloat(item.dailyRate) * days).toFixed(0)}` : 'pricing on request'}
                                            </span>
                                        </span>
                                        <button onClick={() => setCart((c) => ({...c, items: c.items.filter((i) => i.assetTag !== item.assetTag)}))}
                                                className="text-gray-600 hover:text-red-400"><X className="w-4 h-4"/></button>
                                    </li>
                                ))}
                            </ul>
                            {cart.items.length > 0 && (
                                <div className="text-sm border-t border-gray-800 pt-3 mb-4">
                                    <div className="flex justify-between"><span className="text-gray-400">Estimate</span>
                                        <span className="text-lumina-accent font-display font-bold">${estimate.total.toFixed(0)}</span></div>
                                    {estimate.unpriced > 0 && (
                                        <p className="text-[11px] text-gray-500 mt-1">+ {estimate.unpriced} item{estimate.unpriced === 1 ? '' : 's'} priced on request. Nothing is charged until we confirm.</p>
                                    )}
                                </div>
                            )}

                            {viewer ? (
                                <>
                                    <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (so we can reach you)"
                                           className="w-full bg-black border border-gray-800 px-3 py-2 text-sm mb-2 focus:border-lumina-purple outline-none"/>
                                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Shoot details, delivery, questions…"
                                              className="w-full bg-black border border-gray-800 px-3 py-2 text-sm mb-3 focus:border-lumina-purple outline-none"/>
                                    {errors.length > 0 && <ul className="text-red-400 text-xs mb-3 space-y-1">{errors.map((e, i) => <li key={i}>{e}</li>)}</ul>}
                                    <button onClick={submit} disabled={cart.items.length === 0 || !datesValid || inFlight}
                                            className="w-full py-3 bg-lumina-purple text-white font-bold text-sm tracking-widest uppercase hover:bg-lumina-purple/80 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                                        <Check className="w-4 h-4"/> Request booking
                                    </button>
                                    <p className="text-[11px] text-gray-500 mt-2">Requesting as {viewer.name}. We'll confirm by email.</p>
                                </>
                            ) : loginEnabled ? (
                                <>
                                    <p className="text-sm text-gray-400 mb-3">Sign in to send your request — your selection is saved.</p>
                                    <FacebookLoginForm className="w-full py-3 bg-[#1877F2] text-white font-bold text-sm tracking-widest uppercase hover:bg-[#1877F2]/80">
                                        Continue with Facebook
                                    </FacebookLoginForm>
                                </>
                            ) : (
                                <p className="text-sm text-gray-400">Online requests aren't open yet — <a href="/contact" className="text-lumina-accent">contact us</a> and we'll sort it out.</p>
                            )}
                        </div>
                    </aside>
                </div>
            </main>
            <Footer/>
        </div>
    );
};

export default BookPage;
